import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import {
  RazorTable,
  RazorTableRow,
  RazorNumberFormat,
  RazorInputField,
} from "../../../components/razor-reuse";
import FilterReusableComp from "../../../components/FilterReusableComp";
import DateRangeComponent from "../../../components/fragment/date-range/DateRangeComponent";
import PaginationFragment from "../../../components/fragment/pagination/PaginationFragment";
import NoContentPage from "../../../layout/dashboard/no-content/NoContentPage";
import { Button } from "../../../components/custom/custom-button";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import type { AppDispatch } from "../../../redux/store";
import RootState, { NotificationCampaign, NotificationCampaignStatus } from "../../../redux/type";
import {
  getNotificationCampaigns,
  getNotificationCampaignStats,
  deleteNotificationCampaign,
} from "../../../redux/notificationCampaign";
import NotificationStatCard from "./components/NotificationStatCard";
import NotificationChannelBadge from "./components/NotificationChannelBadge";
import NotificationStatusBadge from "./components/NotificationStatusBadge";
import DeleteNotificationCampaignModal from "./DeleteNotificationCampaignModal";

const CAMPAIGNS_PER_PAGE = 10;

const STATUS_FILTER_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Draft", value: "draft" },
  { label: "Scheduled", value: "scheduled" },
  { label: "Active", value: "active" },
  { label: "Sent", value: "sent" },
  { label: "Failed", value: "failed" },
  { label: "Cancelled", value: "cancelled" },
];

const CHANNEL_FILTER_OPTIONS = [
  { label: "Channel", value: "" },
  { label: "Email", value: "email" },
  { label: "Push", value: "push" },
  { label: "In-App", value: "in_app" },
];
export const formatDateTime = (value: string) => {
  const { date, time } = formatCellDate(value);

  return `${date} ${time}`;
};

export function formatCellDate(dateInput: string): { date: string; time: string } {
  const parsed = new Date(dateInput);
  const date = new Intl.DateTimeFormat("en-GB").format(parsed);
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .format(parsed)
    .replace(" ", "");
  return { date, time };
}

export default function NotificationManagementIndex() {
  const [selectedCampaign, setSelectedCampaign] =
    useState<NotificationCampaign | null>(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { campaigns, campaigns_meta, loading_campaigns, stats, loading_stats, loading_delete } =
    useSelector((state: RootState) => state.notification_campaign);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<{ label: string; value: string } | null>(
    null,
  );
  const [channelFilter, setChannelFilter] = useState<{ label: string; value: string } | null>(
    null,
  );
  const [dateRange, setDateRange] = useState("");
  const [page, setPage] = useState(1);

  const handleActionClick = (campaign: any) => {
    setSelectedCampaign(campaign);
    setActionModalOpen(!actionModalOpen);
  };

  const handleUpdate = () => {
    if (!selectedCampaign) return;
    if (selectedCampaign.status !== 'draft') return
    setActionModalOpen(false);

    // Pass the campaign ID to your review/edit page
    navigate(
      `${ALL_ROUTES.DASHBOARD_NOTIFICATION_MANAGEMENT_CREATE}`
      , { state: { action: "update", details: selectedCampaign } });
  };
  const handleDeleteClick = () => {
    setActionModalOpen(false);
    setDeleteModalOpen(true);
  };
  const handleViewCampaign = (id: string | number) => {
    navigate(ALL_ROUTES.DASHBOARD_NOTIFICATION_MANAGEMENT_VIEW, {
      state: { id },
    });
  };
  const handleView = () => {
    if (!selectedCampaign) return;

    setActionModalOpen(false);

    handleViewCampaign(selectedCampaign.id);
  };

  const fetchCampaigns = () => {
    const [from_date, to_date] = dateRange ? dateRange.split(",") : [];

    dispatch(
      getNotificationCampaigns({
        search,
        status: (statusFilter?.value || "") as any,
        channel: (channelFilter?.value || "") as any,
        from_date,
        to_date,
        per_page: CAMPAIGNS_PER_PAGE,
        page,
      }) as any
    );
  };

  const fetchStats = () => {
    dispatch(getNotificationCampaignStats({}) as any);
  };
  const handleDeleteConfirm = async () => {
    if (!selectedCampaign) return;
    try {
      await dispatch(
        deleteNotificationCampaign(selectedCampaign.id)
      )
        .unwrap();

      setDeleteModalOpen(false);
      setSelectedCampaign(null);
      fetchCampaigns()

    } catch (error) {
      console.error("Failed to delete campaign:", error);
    }
  }
  useEffect(() => {
    fetchStats()
  }, [dispatch]);

  useEffect(() => {
    fetchCampaigns()

  }, [dispatch, search, statusFilter, channelFilter, dateRange, page]);
  useEffect(() => {
    const handleOnline = () => {
      fetchStats();
      fetchCampaigns();
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [
    search,
    statusFilter,
    channelFilter,
    dateRange,
    page,
  ]);
  const rows = campaigns || [];

  return (
    <DashboardLayoutMain back onBack={() => navigate(-1)} title="Notifications">
      <section className="mx-[2rem] mt-[15px] pb-[40px]">
        <div className="flex items-start justify-between">
          <p className="text-[24px] font-bold text-[#1E1E1E]">
            Notification Management
          </p>
          <Button
            onClick={() => navigate(ALL_ROUTES?.DASHBOARD_NOTIFICATION_MANAGEMENT_CREATE)}
          >
            <Plus className="size-[16px]" />
            Create Notifications
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-[16px] mt-[20px]">
          <NotificationStatCard
            icon="email"
            label="Total emails sent"
            value={stats?.total_emails_sent}
            loading={loading_stats}
          />
          <NotificationStatCard
            icon="push"
            label="Total push notifications"
            value={stats?.total_push_notifications}
            loading={loading_stats}
          />
          <NotificationStatCard
            icon="in_app"
            label="Total in-app notifications"
            value={stats?.total_in_app_notifications}
            loading={loading_stats}
          />
        </div>

        <div className="mt-[24px]">
          <FilterReusableComp
            searchPlaceholder="Search notifications"
            onSearch={(value) => {
              setSearch(value);
              setPage(1);
            }}
            hideExport
            selectOneList={STATUS_FILTER_OPTIONS.filter(
              (o) => o.label !== statusFilter?.label,
            )}
            selectOneValue={statusFilter}
            defaultSelectOne="All Status"
            onSelectOne={(param) => {
              setStatusFilter(param);
              setPage(1);
            }}
            moreComponents={
              <>
                <DateRangeComponent
                  value={dateRange}
                  onSet={(value) => {
                    setDateRange(value);
                    setPage(1);
                  }}
                  onCancel={() => {
                    setDateRange("");
                    setPage(1);
                  }}
                />
                <RazorInputField
                  color="black-light"
                  className="select-input"
                  type="select"
                  placeholder=" "
                  value={
                    channelFilter || { label: "Channel", value: "" }
                  }
                  onChange={(param: any) => {
                    if (param?.label !== channelFilter?.label) {
                      setChannelFilter(param);
                      setPage(1);
                    }
                  }}
                  selectOption={CHANNEL_FILTER_OPTIONS.filter(
                    (o) => o.label !== channelFilter?.label,
                  )}
                />
              </>
            }
          />
        </div>

        <div className="mt-[16px]">
          {!loading_campaigns && rows.length < 1 ? (
            <NoContentPage
              title="No notifications found"
              text="Notifications will show up here once they're created"
            />
          ) : (
            <>
              <RazorTable
                headerList={[
                  "Notification",
                  "Channel",
                  "Status",
                  "Recipients",
                  "Created",
                  "Last Updated",
                  "Action",
                ]}
              >
                {rows.map((row) => {
                  const created = formatCellDate(row.created_at);
                  const updated = formatCellDate(row.updated_at);
                  return (
                    <RazorTableRow
                      key={row.id}
                      loading={loading_campaigns}
                      onRowClick={() => handleViewCampaign(row.id)}
                      className="cursor-pointer"
                      one={<span className="font-medium">{row.title}</span>}
                      two={
                        <div className="flex flex-wrap gap-[6px]">
                          {row.channels?.map((selection) => (
                            <NotificationChannelBadge
                              key={selection.channel}
                              channel={selection.channel}
                            />
                          ))}
                        </div>
                      }
                      three={<NotificationStatusBadge status={row.status} />}
                      four={RazorNumberFormat(
                        row.recipients_count ||
                          row.audience?.estimated_recipient_count ||
                          0,
                        {
                          hideDecimal: true,
                          hideSymbol: true,
                        },
                      )}
                      five={
                        <span className="whitespace-nowrap">
                          {created.date}
                          <br />
                          <span className="text-[#758089]">{created.time}</span>
                        </span>
                      }
                      six={
                        <span className="whitespace-nowrap">
                          {updated.date}
                          <br />
                          <span className="text-[#758089]">{updated.time}</span>
                        </span>
                      }
                      seven={
                        // Isolated from the row's onRowClick — any click inside
                        // this cell (the trigger or the dropdown items) must
                        // only ever run the action menu's own handlers.
                        <div
                          className="relative"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => handleActionClick(row)}
                            className="flex h-8 w-8 items-center justify-center rounded-md text-[#A1A1AA] hover:bg-gray-100"
                          >
                            <span className="text-xl leading-none">⋯</span>
                          </button>

                          {actionModalOpen && selectedCampaign?.id === row.id && (
                            <ActionModal
                            status={row.status}
                              onView={handleView}
                              onUpdate={handleUpdate}
                              onDelete={handleDeleteClick}
                            />
                          )}
                        </div>
                      }
                    />
                  );
                })}
              </RazorTable>
              <PaginationFragment
                currentPage={campaigns_meta?.current_page || page}
                perPage={campaigns_meta?.per_page || CAMPAIGNS_PER_PAGE}
                totalPage={campaigns_meta?.total || 0}
                hidePagination={loading_campaigns}
                onPageChange={(num) => setPage(num)}
              />
            </>
          )}
        </div>
      </section>
      <DeleteNotificationCampaignModal
        visible={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedCampaign(null);
        }}
        onConfirm={handleDeleteConfirm}
        campaign={selectedCampaign}
        loading={loading_delete}
      />
    </DashboardLayoutMain>
  )
}

const ActionModal = ({
  status,
  onView,
  onUpdate,
  onDelete,
}: {
  status:NotificationCampaignStatus;
  onView: () => void;
  onUpdate: () => void;
  onDelete: () => void;
}) => {
  return (
    <div className="absolute right-0 top-9 z-50 w-[150px] rounded-lg border border-[#E5E7EB] bg-white p-1 shadow-lg">
      <button
        type="button"
        onClick={onView}
        className="flex w-full rounded-md px-3 py-2 text-left text-sm text-[#1E1E1E] hover:bg-gray-100"
      >
        View
      </button>

      {status==='draft'&&<button
        type="button"
        onClick={onUpdate}
        className="flex w-full rounded-md px-3 py-3 text-left text-sm text-[#1E1E1E] hover:bg-gray-100"
      >
        Update
      </button>}

      <button
        type="button"
        onClick={onDelete}
        className="flex w-full rounded-md px-3 py-3 text-left text-sm text-red-600 hover:bg-red-50"
      >
        Delete
      </button>
    </div>
  );
};
